import { useId, type Ref } from "react";
import { ChevronRight } from "lucide-react";
import { formatUsd } from "@/lib/displayFormat";
import { CarrierIdentity } from "@/components/CarrierIdentity";
import { CoverageTermsSummary } from "@/components/CoverageTermsSummary";
import {
  policyTypeLabel,
  coveragePartsOf,
  termsTotalsOf,
} from "@/lib/submissions/coverageRows";
import { QuoteChipBadge } from "@/components/submissions/QuoteChipBadge";
import { QuoteProvenance } from "@/components/submissions/QuoteProvenance";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import {
  showsPrice,
  type QuoteCard as Card,
} from "@/lib/submissions/quoteCards";

// BRK-S347 — one carrier's offer as a card: who it is, where it stands, its
// total cost and a few facts. The whole card is one button that opens the
// quote popup, so it holds no other control: every action is in the popup,
// beside what it means.

export { QuoteChipBadge } from "@/components/submissions/QuoteChipBadge";
export { Monogram } from "@/components/CarrierIdentity";

/** A figure from a quote. An outdated quote's stays for reference, struck
 *  through and announced as outdated, never as the current price
 *  (BRK-S341). */
export function QuoteFigure({
  quote,
  text,
}: {
  quote: BrokerQuote | null;
  text: string;
}) {
  if (!quote?.outdated) return <span>{text}</span>;
  return (
    <>
      <span className="sr-only">Outdated: </span>
      <s className="text-brk-dim">{text}</s>
    </>
  );
}

export function QuoteCard({
  card,
  onOpen,
  buttonRef,
}: {
  card: Card;
  onOpen: () => void;
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  const id = useId();
  const hint = card.run?.kind === "answering" ? "Quote in progress" : null;
  return (
    <button
      ref={buttonRef}
      id={card.quote ? `quote-${card.quote.id}` : undefined}
      type="button"
      aria-haspopup="dialog"
      onClick={onOpen}
      className="flex h-full w-full flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-4 text-left transition-colors hover:border-brk-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brk-accent motion-reduce:transition-none"
    >
      <span className="flex w-full items-start justify-between gap-3">
        <CarrierIdentity
          name={card.carrier}
          profile={card.carrierProfile}
          nameId={`${id}-name`}
        />
        <QuoteChipBadge id={`${id}-chip`} chip={card.chip} />
      </span>

      {showsPrice(card) ? (
        <span className="flex flex-col">
          <span id={`${id}-total`} className="flex flex-col">
            {/* As tall as the Lowest price tag, so every card's figure lines
                up with its neighbours'. */}
            <span className="flex min-h-6 flex-wrap items-center gap-2">
              <span className="text-xs text-brk-dim">Total cost</span>
              {card.lowest && (
                <span
                  id={`${id}-lowest`}
                  className="rounded-full border border-brk-accent px-2 py-0.5 text-xs font-medium text-brk-accent"
                >
                  Lowest price
                </span>
              )}
            </span>
            <span className="text-figure tabular-nums text-brk-text">
              <QuoteFigure
                quote={card.quote}
                text={formatUsd(
                  card.quote.terms?.totals?.totalCost ?? card.quote.totalCost,
                )}
              />
            </span>
          </span>
        </span>
      ) : (
        <span id={`${id}-total`} className="line-clamp-3 text-sm text-brk-text">
          {card.summary ?? "No price from this carrier."}
        </span>
      )}

      <span id={`${id}-more`} className="flex w-full flex-col gap-3">
        {card.quote?.stage && (
          <QuoteProvenance quote={card.quote} statusLabel={card.chip.label} />
        )}
        {showsPrice(card) && termsTotalsOf(card.quote.terms).length > 0 ? (
          <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-brk-dim">
            {termsTotalsOf(card.quote.terms)
              .filter((r) => r.key !== "totalCost")
              .map((row) => (
                <span key={row.key}>
                  {row.label}{" "}
                  <QuoteFigure quote={card.quote} text={formatUsd(row.value)} />
                </span>
              ))}
          </span>
        ) : (
          <>
            {showsPrice(card) && (
              <span className="text-sm tabular-nums text-brk-dim">
                Premium{" "}
                <QuoteFigure
                  quote={card.quote}
                  text={formatUsd(
                    card.quote.terms?.totals?.totalPremium ??
                      card.quote.totalPremium,
                  )}
                />{" "}
                · Taxes &amp; fees{" "}
                <QuoteFigure
                  quote={card.quote}
                  text={formatUsd(card.quote.taxesAndFees)}
                />
              </span>
            )}
          </>
        )}
        {showsPrice(card) && coveragePartsOf(card.quote.terms).length > 0 && (
          <>
            <span className="text-xs text-brk-dim">
              {policyTypeLabel(card.quote.terms)}
            </span>
            <CoverageTermsSummary
              terms={card.quote.terms}
              compact
              marketType={card.carrierProfile?.market?.type}
            />
          </>
        )}
        {/* A priced card's summary is a referral's: whose underwriter has to
            review it, under its figure (BRK-S354). */}
        {showsPrice(card) && card.summary && (
          <span className="text-sm text-brk-text">{card.summary}</span>
        )}
        {card.facts.length > 0 && (
          <span className="flex flex-col gap-1.5 border-t border-brk-border pt-3 text-sm">
            {card.facts.map((fact) => (
              <span
                key={fact.label}
                className="flex flex-wrap justify-between gap-x-3"
              >
                <span className="text-brk-dim">{fact.label}</span>
                <span className="text-right text-brk-text">{fact.value}</span>
              </span>
            ))}
          </span>
        )}
        {card.conditions && (
          <span className="text-sm text-brk-text">{card.conditions}</span>
        )}
        {hint && <span className="text-sm text-brk-dim">{hint}</span>}
      </span>

      <span className="mt-auto inline-flex items-center gap-1 self-end text-sm font-medium text-brk-accent">
        View details
        <ChevronRight aria-hidden="true" className="h-4 w-4" />
      </span>
    </button>
  );
}
